/**
 * First-party SPA OAuth — runs the same authorization-code + PKCE +
 * magic-link flow that MCP clients use, but against a stable Cognito
 * app client provisioned for the website (`FrontendClient` in the auth
 * stack). Used by `/manage` so the user can hit the Stripe billing portal
 * with a Bearer token instead of an API key.
 *
 * Flow:
 *   1. signIn() — generate PKCE verifier + challenge, stash verifier in
 *      localStorage (see Storage choice below), navigate to /oauth/authorize
 *      on the auth host.
 *   2. The auth host redirects to /login (this app), email is entered, a
 *      magic link is mailed, user clicks → /login/complete → which mints
 *      an auth code and navigates back to /auth/callback?code=...&state=...
 *   3. handleCallback() — POST to /oauth/token with code + verifier, store
 *      access/refresh tokens in sessionStorage, then clear the localStorage
 *      flow state.
 *
 * Storage choice:
 *   - OAuth flow state (PKCE verifier, CSRF state, return URL) → localStorage.
 *     The magic-link callback opens in a NEW tab, and sessionStorage is
 *     scoped per-tab — Tab B can't read what Tab A stored. localStorage
 *     is shared across tabs, so the new tab can complete the flow.
 *     handleCallback removes these immediately after exchanging the code,
 *     so the localStorage residue is short-lived in practice.
 *   - Access + refresh tokens → sessionStorage. Tokens are sensitive
 *     (a refresh token can mint access tokens for ~30 days), so we limit
 *     their lifetime to the browser session. Cross-tab persistence isn't
 *     needed once the OAuth handshake is done — the user just stays in
 *     whichever tab they end up using.
 */

const AUTH_URL: string = import.meta.env.VITE_AUTH_URL ?? '';
const FRONTEND_CLIENT_ID: string = import.meta.env.VITE_FRONTEND_CLIENT_ID ?? '';
const MCP_SCOPE: string = import.meta.env.VITE_MCP_SCOPE ?? '';

// OAuth flow state — MUST live in localStorage, not sessionStorage. The
// magic-link email opens the callback in a NEW tab (Tab B) while the
// flow was started in the original tab (Tab A); sessionStorage is scoped
// per-tab so Tab B can't read what Tab A stored. Using localStorage lets
// the new tab pick up the verifier + state for the PKCE exchange and
// CSRF check. These values are short-lived in practice — handleCallback
// clears them as soon as the code is exchanged.
const PKCE_KEY = 'draftlion:pkce_verifier';
const STATE_KEY = 'draftlion:oauth_state';
const RETURN_KEY = 'draftlion:return_to';

// Tokens — stay in sessionStorage. Tokens are sensitive (refresh tokens
// can mint access tokens for ~30 days), so we deliberately limit their
// lifetime to the browser session. Cross-tab persistence isn't needed
// for tokens because once we're past the OAuth callback, the user only
// needs them in whichever tab they end up using.
const ACCESS_KEY = 'draftlion:access_token';
const REFRESH_KEY = 'draftlion:refresh_token';
const EXPIRES_KEY = 'draftlion:access_expires_at';

// ------------------------------------------------------------------
// PKCE helpers
// ------------------------------------------------------------------

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function challengeFromVerifier(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64url(new Uint8Array(hash));
}

// ------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------

/** True iff we have an access token that hasn't expired yet. */
export function isSignedIn(): boolean {
  const token = sessionStorage.getItem(ACCESS_KEY);
  const exp = Number(sessionStorage.getItem(EXPIRES_KEY) ?? 0);
  // 30s slack — refresh proactively rather than mid-request.
  return !!token && Date.now() < exp - 30_000;
}

/**
 * Returns a valid access token, refreshing if needed. Returns null when
 * the user isn't signed in (caller should redirect to signIn()).
 */
export async function getAccessToken(): Promise<string | null> {
  if (isSignedIn()) return sessionStorage.getItem(ACCESS_KEY);

  const refresh = sessionStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;

  // Refresh the access token. Cognito refresh tokens are bound to the
  // client_id that issued them, so we send our frontend client_id here.
  try {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refresh,
      client_id: FRONTEND_CLIENT_ID,
    });
    const res = await fetch(`${AUTH_URL}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      // Refresh failed (revoked, expired, etc) — wipe and force re-login.
      signOut();
      return null;
    }
    const data = await res.json();
    storeTokens(data);
    return sessionStorage.getItem(ACCESS_KEY);
  } catch {
    signOut();
    return null;
  }
}

/**
 * Kick off the OAuth + magic-link sign-in flow. Pass `returnTo` to navigate
 * the user back to a specific page after they finish authenticating.
 */
export async function signIn(returnTo: string = window.location.pathname): Promise<void> {
  if (!AUTH_URL || !FRONTEND_CLIENT_ID) {
    throw new Error('Auth not configured: missing VITE_AUTH_URL or VITE_FRONTEND_CLIENT_ID');
  }

  const verifier = randomString(32);
  const state = randomString(16);
  const challenge = await challengeFromVerifier(verifier);

  // localStorage (not session): the magic-link callback opens in a new tab.
  localStorage.setItem(PKCE_KEY, verifier);
  localStorage.setItem(STATE_KEY, state);
  localStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: FRONTEND_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/callback`,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    scope: MCP_SCOPE,
  });
  window.location.assign(`${AUTH_URL}/oauth/authorize?${params.toString()}`);
}

/**
 * Called by the /auth/callback page on mount. Reads ?code=&state= from the
 * URL, exchanges the code for tokens, returns the path to navigate to.
 */
export async function handleCallback(): Promise<{ returnTo: string }> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (error) {
    const description = url.searchParams.get('error_description') ?? '';
    throw new Error(`Sign-in failed (${error}): ${description}`);
  }
  if (!code) throw new Error('Missing authorization code');

  const expectedState = localStorage.getItem(STATE_KEY);
  if (!state || state !== expectedState) {
    throw new Error('State mismatch — possible CSRF, refusing to continue');
  }

  const verifier = localStorage.getItem(PKCE_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier — start the sign-in over');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: `${window.location.origin}/auth/callback`,
    client_id: FRONTEND_CLIENT_ID,
    code_verifier: verifier,
  });
  const res = await fetch(`${AUTH_URL}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    let msg = `Token exchange failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.error_description) msg = j.error_description;
      else if (j?.error) msg = j.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  const data = await res.json();
  storeTokens(data);

  const returnTo = localStorage.getItem(RETURN_KEY) || '/manage';
  localStorage.removeItem(PKCE_KEY);
  localStorage.removeItem(STATE_KEY);
  localStorage.removeItem(RETURN_KEY);
  return { returnTo };
}

/** Wipe all auth state. Doesn't revoke server-side — Cognito refresh tokens
 *  expire on their own (30 days) and access tokens expire in an hour. */
export function signOut(): void {
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(EXPIRES_KEY);
}

// ------------------------------------------------------------------
// Internal
// ------------------------------------------------------------------

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

function storeTokens(data: TokenResponse): void {
  if (!data.access_token) throw new Error('Token response missing access_token');
  sessionStorage.setItem(ACCESS_KEY, data.access_token);
  // Refresh token is only present on the first exchange (and sometimes on
  // refresh, depending on Cognito settings). Keep the existing one if the
  // response omits it.
  if (data.refresh_token) sessionStorage.setItem(REFRESH_KEY, data.refresh_token);
  const ttlSeconds = data.expires_in ?? 3600;
  sessionStorage.setItem(EXPIRES_KEY, String(Date.now() + ttlSeconds * 1000));
}
